import { useSelector } from "react-redux";
import { useFetchRolesQuery } from "../../Apis/rolesApi";
import { RolesList } from "../../Model/userObjects";
import { enterpriseLevelsDetailsSelector } from "../../Reducers/enterpriseLevelsDetailsReducer";
import { useMemo, useCallback } from "react";
import { ENTERPRISE, REGIONAL, SUB_REGIONAL } from "../../Enums/administratorLevels";
import { USER } from "../../Enums/accessLevels";
import CustomSelect from "../CommonComponents/select/CustomSelect";
import PlusIcon from "../Icons/plus-icon";
import CrossIcon from "../Icons/cross-icon";
import DeleteIcon from "../Icons/delete-icon";
import { cx } from "../../Util/classHelpers";
import styles from "./users-and-roles.module.css";
import { MODAL_ACTIONS, USER_ACTIONS } from "./UserInvitationModal";
import { useSendMultipleUserInvitationsMutation } from "../../Apis/userInvitationsApi";

const administratorLevelOptions = [
  { value: ENTERPRISE, label: "Enterprise" },
  { value: REGIONAL, label: "Region" },
  { value: SUB_REGIONAL, label: "Subregion" },
]

const SingleUserForm = ({user, usersDispatch, modalDispatch, showDelete}) => {
  const { data: roles = new RolesList() } = useFetchRolesQuery();
  const enterpriseLevelsDetails = useSelector(enterpriseLevelsDetailsSelector);

  const roleOptions = useMemo(() => {
    return roles.getRoles().map(role => ({
      value: role.getId(),
      label: role.getName(),
    }));
  }, [roles]);

  const administratorLevelDisplayFunc = useCallback(() => {
    if (enterpriseLevelsDetails === null) return "";
    const level = user.administratorLevel;
    const id = user.accessId;
    if (level === null || id === null) return "";
    return enterpriseLevelsDetails.getInfo(level, id).name;
  }, [enterpriseLevelsDetails, user]);

  const selectAdministratorLevel = useCallback((value) => {
    let updatedUser;
    switch (value) {
      case ENTERPRISE:
        updatedUser = { ...user, accessId: enterpriseLevelsDetails.getEnterprise().getId(), administratorLevel: ENTERPRISE };
        usersDispatch({ 
          type: USER_ACTIONS.UPDATE_USER, 
          payload: { id: user.id, user: updatedUser } 
        });
        break;
      case REGIONAL:
        modalDispatch({ type: MODAL_ACTIONS.SELECT_REGION, payload: user.id });
        break;
      case SUB_REGIONAL:
        modalDispatch({ type: MODAL_ACTIONS.SELECT_SUBREGION, payload: user.id });
        break;
      default:
        return;
    }
  }, [usersDispatch, user, enterpriseLevelsDetails, modalDispatch]);

  return (
    <div className={styles.inviteFormUserRow}>
      <div className={styles.inviteFormInputGroup}>
        <input
          type="text"
          placeholder="Name"
          className={styles.inviteFormInput}
          value={user.name}
          onChange={(e) => {
            usersDispatch({ 
              type: USER_ACTIONS.UPDATE_USER, 
              payload: { id: user.id, user: { ...user, name: e.target.value } } 
            });
          }}
        />
        <input
          type="email"
          placeholder={"Email address"}
          className={styles.inviteFormInput}
          value={user.email}
          onChange={(e) => {
            usersDispatch({ 
              type: USER_ACTIONS.UPDATE_USER, 
              payload: { id: user.id, user: { ...user, email: e.target.value } } 
            });
          }}
        />
        <CustomSelect
          options={roleOptions}
          selected={user.roleId}
          onSelect={(value) => {
            usersDispatch({ 
              type: USER_ACTIONS.UPDATE_USER, 
              payload: { id: user.id, user: { ...user, roleId: value } } 
            });
          }}
          selectClassName={cx(
            styles.inviteFormCustomSelect,
            styles.inviteFormRoleSelect
          )}
          label="Select a role"
        />
        <CustomSelect
          options={administratorLevelOptions}
          selected={user.administratorLevel}
          onSelect={selectAdministratorLevel}
          selectClassName={cx(
            styles.inviteFormCustomSelect,
            styles.inviteFormAccessSelect
          )}
          label="Select access"
          displayFunc={administratorLevelDisplayFunc}
        />
      </div>
      {showDelete && (
        <div className={styles.inviteFormDeleteButton} role="button" onClick={() => {
          usersDispatch({ 
            type: USER_ACTIONS.DELETE_USER, 
            payload: { id: user.id } 
          });
        }}>
          <DeleteIcon />
        </div>
      )}
    </div>
  );
}

export const UserInvitationForm = ({
  onClose,
  users,
  usersDispatch,
  modalDispatch,
  onInvitationsSent,
  onInvitationSendError,
}) => {
  const [sendUserInvitations, { isLoading: isSendingInvitations }] = useSendMultipleUserInvitationsMutation();

  const disabled = users.some(user => !user.name || !user.email || !user.roleId || !user.accessId || !user.administratorLevel);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (disabled || isSendingInvitations) return;

    const invitationsData = users.map(user => ({
      name: user.name,
      email: user.email,
      accessId: user.accessId,
      administratorLevel: user.administratorLevel,
      accessLevel: USER,
      roleId: user.roleId
    }));

    try {
      await sendUserInvitations(invitationsData).unwrap();
      onInvitationsSent?.();
    } catch (error) {
      onInvitationSendError?.(error);
    }
  }

  return (
    <form className={styles.inviteFormContainer} onSubmit={handleSubmit}>
      <div className={styles.inviteFormHeader}>
        <div className={styles.inviteFormHeaderContent}>
          <div className={styles.inviteFormTitle}>
            <h2 className={styles.inviteFormTitleText}>Invite users</h2>
          </div>
        </div>
        <button className={styles.inviteFormCloseButton} type="button" onClick={onClose}>
          <CrossIcon />
        </button>
      </div>

      <div className={styles.inviteFormContent}>
        <div className={styles.inviteFormDescription}>
          Invite new team members by assigning their role and access scope.
        </div>

        <div className={styles.inviteFormUsers}>
          {users.map((user) => (
            <SingleUserForm
              key={user.id}
              user={user}
              usersDispatch={usersDispatch}
              modalDispatch={modalDispatch}
              showDelete={users.length > 1}
            />
          ))}

          <div className={styles.inviteFormAddUser}>
            <button
              className={styles.inviteFormAddUserButton}
              type="button"
              onClick={() => usersDispatch({ type: USER_ACTIONS.ADD_USER })}
            >
              <PlusIcon fill="#03624C" />
              <span className={styles.inviteFormAddUserText}>Add user</span>
            </button>
          </div>
        </div>
      </div>

      <div className={styles.inviteFormFooter}>
        <button className={styles.inviteFormCancelButton} type="button" onClick={onClose}>
          Cancel
        </button>
        <div className={styles.inviteFormFooterRight}>
          <button 
            className={styles.inviteFormInviteButton} 
            type="submit" 
            disabled={disabled || isSendingInvitations}
          >
            {isSendingInvitations ? 'Sending invitations...' : 'Invite users'}
          </button>
        </div>
      </div>
    </form>
  );
}
